import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { PayoutRatesService } from '@examdojo/payments';
import { LoaderComponent } from '@examdojo/ui/loader';
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowClickedEvent, ValueGetterParams } from 'ag-grid-community';
import { finalize, forkJoin, from, tap } from 'rxjs';
import { CmsUrlParts } from '../../cms.model';
import { AdminUserUIModel } from '../admin-user.model';
import { AdminUserQuery } from '../admin-user.query';
import { AdminUserService } from '../admin-user.service';
import { InviteUsersDialogService } from '../invite-users-dialog.service';
import { AdminListViewService } from './admin-list-view.service';
import { ProgramBadgeCellRendererComponent } from './ag-grid-renderer/program-badge-cell-renderer.component';
import { RoleBadgeCellRendererComponent } from './ag-grid-renderer/role-badge-cell-renderer.component';

@Component({
  selector: 'exam-admin-list-view',
  templateUrl: './admin-list-view.component.html',
  styleUrl: './admin-list-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [MasterListViewComponent, LoaderComponent, MatSlideToggle, ReactiveFormsModule],
  providers: [provideSimpleListView(AdminListViewService), InviteUsersDialogService],
  animations: [getAnimationAppearOnTrigger()],
})
export class AdminListViewComponent {
  constructor(
    private readonly adminUserService: AdminUserService,
    private readonly adminUserQuery: AdminUserQuery,
    private readonly payoutRatesService: PayoutRatesService,
    private readonly destroyRef: DestroyRef,
    private readonly dateTimeService: DateTimeService,
    private readonly router: Router,
    private readonly inviteUsersDialogService: InviteUsersDialogService,
  ) {
    this.setupExternalUsersFilterEffect();
  }

  readonly gridApi = signal<GridApi<AdminUserUIModel> | null>(null);
  readonly excludeInternalUsersCtrl = new FormControl<boolean>(true, { nonNullable: true });
  readonly defaultSize = 's' as const;
  readonly isLoading = signal<boolean>(false);

  readonly state = connectState({
    adminUsers: this.adminUserQuery.entities$,
    isLoading: this.adminUserQuery.selectLoading(),
  });

  readonly columnDefs: Array<ColDef<AdminUserUIModel>> = [
    {
      colId: 'name',
      headerName: 'Name',
      valueGetter: ({ data }: { data: AdminUserUIModel | undefined }) => {
        if (!data) {
          return '';
        }
        const firstName = data.first_name || '';
        const lastName = data.last_name || '';
        return `${firstName} ${lastName}`.trim() || '';
      },
      minWidth: 150,
      filterParams: {
        filterOptions: ['contains'],
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      valueGetter: ({ data }) => data?.email ?? '',
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      minWidth: 250,
      filterParams: {
        filterOptions: [
          'contains',
          {
            displayKey: 'internal',
            displayName: 'Internal users',
            predicate: (_: unknown, cellValue: string) =>
              (cellValue !== null && (cellValue.endsWith('@tellmira.com') || cellValue.endsWith('@examdojo.com'))) ||
              cellValue.endsWith('@dojostack.com'),
            numberOfInputs: 0,
          },
          {
            displayKey: 'external',
            displayName: 'External users',
            predicate: (_: unknown, cellValue: string) =>
              cellValue !== null &&
              !cellValue.endsWith('@tellmira.com') &&
              !cellValue.endsWith('@examdojo.com') &&
              !cellValue.endsWith('@dojostack.com'),
            numberOfInputs: 0,
          },
        ],
      },
    },
    {
      field: 'role',
      headerName: 'Role',
      valueGetter: ({ data }) => data?.role ?? '',
      filter: 'agSetColumnFilter',
      floatingFilter: false,
      width: 130,
      cellClass: 'truncate',
      cellRenderer: RoleBadgeCellRendererComponent,
      cellRendererParams: {
        config: { size: 'sm' },
      },
    },
    {
      field: 'assignedSyllabuses',
      headerName: 'Assigned Programs',
      cellRenderer: ProgramBadgeCellRendererComponent,
      cellRendererParams: {
        config: { size: 'sm', showIcon: true },
      },
      valueGetter: ({ data }) => {
        // ADMIN users have access to all programs
        if (data?.role === 'ADMIN') {
          return ['All'];
        }
        return data?.assignedSyllabuses || [];
      },
      filterValueGetter: (params: ValueGetterParams<AdminUserUIModel>) => {
        // ADMIN users have access to all programs
        if (params.data?.role === 'ADMIN') {
          return 'All';
        }
        return `${params.data?.assignedSyllabuses
          ?.map((syllabus) => syllabus.program.code)
          .filter(isNotNullish)
          .join(', ')}, ${params.data?.assignedSyllabuses
          ?.map((syllabus) => syllabus.name)
          .filter(isNotNullish)
          .join(', ')}`;
      },
      filter: 'agTextColumnFilter',
      minWidth: 220,
      cellClass: 'flex items-center',
      filterParams: {
        filterOptions: ['contains'],
      },
    },
    {
      field: 'joined_date',
      headerName: 'Joined Date',
      valueFormatter: ({ data }) => {
        if (!data?.joined_date) {
          return '';
        }

        return this.dateTimeService.formatDate(data.joined_date, DateTimeFormat.ShortDateTime);
      },
      filterParams: {
        comparator: (filterDate: Date, cellValue: string) => {
          if (!cellValue) {
            return -1;
          }

          const cellDate = new Date(cellValue);

          const cellDateOnly = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
          const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());

          const cellTime = cellDateOnly.getTime();
          const filterTime = filterDateOnly.getTime();

          if (cellTime < filterTime) {
            return -1;
          } else if (cellTime > filterTime) {
            return 1;
          }
          return 0;
        },
      },
      filter: 'agDateColumnFilter',
      floatingFilter: false,
      width: 140,
    },
    {
      field: 'payoutRate',
      headerName: 'Payout Rate',
      valueFormatter: ({ data }) => {
        if (!data?.payoutRate) {
          return '—';
        }
        return `€${data.payoutRate.toFixed(2)}/hour`;
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: false,
      width: 120,
      type: 'numericColumn',
    },
  ];

  readonly gridOptions: GridOptions<AdminUserUIModel> = {
    suppressCellFocus: true,
    suppressContextMenu: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No admin users found',
      body: 'No admin users are currently registered in the system',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      floatingFilter: false,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    rowClass: 'cursor-pointer',
    getRowId: (params) => (params.data?.id ? `${params.data.id}` : ''),
  };

  openAdminUser(event: RowClickedEvent<AdminUserUIModel, void>) {
    if (!event.data) {
      return;
    }

    this.router.navigate([CmsUrlParts.Admin, event.data.id]);
  }

  onGridReady(evt: GridReadyEvent) {
    this.gridApi.set(evt.api);
    this.applyExternalUsersFilter();
    // Auto-size columns to fit available space
    evt.api.sizeColumnsToFit();
  }

  onAddUser() {
    from(this.inviteUsersDialogService.openDialog())
      .pipe(
        tap((invited) => {
          if (invited) {
            this.loadAdminUsers();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadAdminUsers() {
    this.isLoading.set(true);

    forkJoin([this.adminUserService.fetchAll(), this.payoutRatesService.fetchAll()])
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupExternalUsersFilterEffect(): void {
    this.excludeInternalUsersCtrl.valueChanges
      .pipe(
        tap(() => {
          this.applyExternalUsersFilter();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private applyExternalUsersFilter(): void {
    const api = this.gridApi();
    if (!api) {
      return;
    }

    const filterModel = api.getFilterModel();

    if (this.excludeInternalUsersCtrl.value) {
      // Apply external users filter
      filterModel['email'] = { type: 'external' };
    } else {
      // Remove the filter
      delete filterModel['email'];
    }

    api.setFilterModel(filterModel);
  }
}
